import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Card,
  CardContent,
  Chip,
  Grid
} from '@mui/material';
import { useAuth } from '../../components/auth/AuthContext';
import { Order, OrderResponse } from '../../types/order';
import { APIHOST } from '../../common';

interface UserOrderSummaryProps {
  order: Order;
  onClick: () => void;
}

function UserOrderSummary({ order, onClick }: UserOrderSummaryProps) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'info';
      case 'received':
        return 'primary';
      case 'abandoned':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card 
      sx={{ 
        mb: 2, 
        cursor: 'pointer',
        '&:hover': { 
          boxShadow: 6,
          transform: 'translateY(-2px)'
        },
        transition: 'all 0.2s ease-in-out'
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="h3" gutterBottom>
              Confirmation #: {order.confirmation}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {order.conference_group.conference?.name || 'Conference'}
            </Typography>
          </Box>
          <Chip 
            label={order.orderStatus} 
            color={getStatusColor(order.orderStatus) as any}
            size="small"
          />
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight="medium">
              Rooms:
            </Typography>
            <Typography variant="body2">
              {order.roomCount || order.order_rooms?.length || 0}
            </Typography>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight="medium">
              Occupants:
            </Typography>
            <Typography variant="body2">
              {order.occupantCount || 0}
            </Typography>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight="medium">
              Created:
            </Typography>
            <Typography variant="body2">
              {formatDate(order.createdAt)}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight="medium">
              Hotel:
            </Typography>
            <Typography variant="body2">
              {order.conference_hotel?.hotel?.name || 'TBD'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function UserOrdersPage() {
  const navigate = useNavigate();
  const { currentUser, isLoggedIn } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!isLoggedIn || !currentUser) {
      navigate('/');
      return;
    }
    fetchUserOrders();
  }, [isLoggedIn, currentUser, navigate]);

  const fetchUserOrders = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders?filters[orderStatus][$eq]=received&sort=createdAt:desc&populate[]=conference_group.conference&populate[]=conference_hotel.hotel&populate[]=order_rooms`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data: OrderResponse = await response.json();
      setOrders(data.data || []);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
      console.error('Orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (order: Order) => {
    navigate(`/user/order/${order.documentId}`);
  };

  if (!isLoggedIn || !currentUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to view your orders.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, my: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            My Orders
          </Typography>
          <Typography variant="subtitle1">
            View and manage your conference room reservations
          </Typography>
        </Box>
        
        <Paper sx={{ p: 3 }}>
          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Orders List */}
          {!loading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {orders.length} Order{orders.length !== 1 ? 's' : ''} Found
              </Typography>
              
              {orders.length === 0 ? (
                <Alert severity="info">
                  No orders found. You haven't made any reservations yet.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {orders.map((order) => (
                    <UserOrderSummary 
                      key={order.documentId} 
                      order={order} 
                      onClick={() => handleOrderClick(order)}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}